import type { HTMLAttributes, MouseEvent, ReactElement, ReactNode } from 'react';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { AlertIcon, CheckCircleIcon, CloseIcon, InfoCircleIcon, WarningIcon } from '../../icons';
import { classNames } from '../../utils/class-names';
import { IconButton, LinkStyleButton } from '../buttons';
import { Tooltip } from '../tooltip';

import styles from './note.module.css';

type Variant = 'neutral' | 'primary' | 'positive' | 'negative' | 'warning' | 'info';

export type NoteProps = {
  variant?: Variant;
  title?: ReactNode;
  icon?: ReactElement;
  testId?: string;
  dismissLabel?: ReactNode;
  onClose?: (event: MouseEvent) => void;
  onDismiss?: (event: MouseEvent) => void;
} & HTMLAttributes<HTMLDivElement>;

const icons: Record<Variant, ReactElement> = {
  neutral: <InfoCircleIcon />,
  primary: <InfoCircleIcon />,
  positive: <CheckCircleIcon />,
  negative: <WarningIcon />,
  warning: <AlertIcon />,
  info: <InfoCircleIcon />,
};

/**
 * @param {Variant} [variant] The note's variant
 * @default 'neutral'
 * @param {ReactNode} [title] The note's title
 * @default undefined
 * @param {ReactElement} [icon] The note's icon
 * @default undefined
 * @param {string} [testId] The test ID used for testing purposes
 * @default ''
 * @param {ReactElement} [dismissLabel] The label for the dismiss button
 * @default undefined
 * @param {() => void} [onClose] Callback to be called when the note is closed
 * @default undefined
 * @param {() => void} [onDismiss] Callback to be called when the note is dismissed
 * @default undefined
 */
export function Note({
  variant = 'neutral',
  title,
  icon,
  testId = '',
  className,
  dismissLabel,
  children,
  onClose,
  onDismiss = onClose,
  ...props
}: NoteProps) {
  const { t } = useTranslation();

  return (
    <div
      data-testid={testId}
      className={classNames(styles.note, styles[variant], title && styles.hasTitle, className)}
      {...props}
    >
      {icon || icons[variant]}

      <div className={styles.content}>
        {title && <h2 className={styles.title}>{title}</h2>}
        {children && <span data-testid={`${testId}-message`}>{children}</span>}

        {onDismiss && (
          <LinkStyleButton className={styles.linkButton} data-testid={`${testId}-dismiss-button`} onClick={onDismiss}>
            {dismissLabel || t('note.button.dismiss')}
          </LinkStyleButton>
        )}
      </div>

      {onClose && (
        <Tooltip label={t('note.button.close')}>
          <IconButton
            aria-label={t('note.button.close')}
            data-testid={`${testId}-close-button`}
            rounded
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
}
